import { ChevronDown, ChevronRight, Search } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import citriniService from "../../../../services/citriniService";
import {
  CitriniPortfolioListItem,
  CitriniPortfolioWithDetails,
} from "../../../../types/citrini";
import { EmptyState, ErrorBanner, formatDateCell, LoadingSpinner } from "../helpers";

interface PortfoliosFetchedTabProps {
  fetchInfoId: number;
  isDarkMode: boolean;
  onCountChange?: (count: number) => void;
}

const PortfoliosFetchedTab: React.FC<PortfoliosFetchedTabProps> = ({
  fetchInfoId,
  isDarkMode,
  onCountChange,
}) => {
  const [portfolios, setPortfolios] = useState<CitriniPortfolioListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [expandedData, setExpandedData] = useState<
    Map<number, CitriniPortfolioWithDetails>
  >(new Map());
  const [expandLoading, setExpandLoading] = useState<Set<number>>(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await citriniService.listPortfoliosByFetchInfoId(fetchInfoId);
      setPortfolios(res);
      onCountChange?.(res.length);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load portfolios");
    } finally {
      setLoading(false);
    }
  }, [fetchInfoId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleRow = useCallback(
    async (portfolio: CitriniPortfolioListItem) => {
      const id = portfolio.id;
      setExpandedRows((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });

      if (!expandedData.has(id) && !expandLoading.has(id)) {
        setExpandLoading((prev) => new Set(prev).add(id));
        try {
          const detail = await citriniService.getPortfolioById(id);
          setExpandedData((prev) => new Map(prev).set(id, detail));
        } catch {
          // Silently fail
        } finally {
          setExpandLoading((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
        }
      }
    },
    [expandedData, expandLoading]
  );

  const expandAll = useCallback(async () => {
    const ids = new Set(portfolios.map((p) => p.id));
    setExpandedRows(ids);
    const toFetch = portfolios.filter(
      (p) => !expandedData.has(p.id) && !expandLoading.has(p.id)
    );
    if (toFetch.length === 0) return;
    const loadingIds = new Set(toFetch.map((p) => p.id));
    setExpandLoading((prev) => new Set([...prev, ...loadingIds]));
    await Promise.all(
      toFetch.map(async (p) => {
        try {
          const detail = await citriniService.getPortfolioById(p.id);
          setExpandedData((prev) => new Map(prev).set(p.id, detail));
        } catch {
          // skip
        } finally {
          setExpandLoading((prev) => {
            const next = new Set(prev);
            next.delete(p.id);
            return next;
          });
        }
      })
    );
  }, [portfolios, expandedData, expandLoading]);

  const collapseAll = useCallback(() => {
    setExpandedRows(new Set());
  }, []);

  const [searchTerm, setSearchTerm] = useState("");

  const filteredPortfolios = React.useMemo(() => {
    if (!searchTerm.trim()) return portfolios;
    const term = searchTerm.toLowerCase();
    return portfolios.filter(
      (p) => p.citriniId.toLowerCase().includes(term) || p.name.toLowerCase().includes(term)
    );
  }, [portfolios, searchTerm]);

  if (loading) {
    return <LoadingSpinner isDarkMode={isDarkMode} />;
  }

  if (error) {
    return <ErrorBanner message={error} onRetry={load} isDarkMode={isDarkMode} />;
  }

  if (portfolios.length === 0) {
    return <EmptyState message="No portfolios fetched in this run" isDarkMode={isDarkMode} />;
  }

  const COLS = 6;

  return (
    <div>
      {/* Search and controls */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <span className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            {filteredPortfolios.length === portfolios.length
              ? `${portfolios.length} portfolio${portfolios.length !== 1 ? "s" : ""}`
              : `${filteredPortfolios.length} of ${portfolios.length}`}
          </span>
          <div className="relative">
            <Search className={`w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-rp-grey/40" : "text-gray-400"}`} />
            <input
              type="text"
              placeholder="Search by ID or name"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`text-xs rounded-lg border pl-8 pr-3 py-1.5 w-52 ${
                isDarkMode
                  ? "bg-rp-surface border-rp-separator text-rp-grey/70 placeholder-rp-grey/40"
                  : "bg-white border-gray-300 text-gray-700 placeholder-gray-400"
              }`}
            />
          </div>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={expandAll}
            className={`text-xs px-2 py-1 rounded ${
              isDarkMode ? "text-rp-grey/50 hover:bg-rp-surface" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className={`text-xs px-2 py-1 rounded ${
              isDarkMode ? "text-rp-grey/50 hover:bg-rp-surface" : "text-gray-500 hover:bg-gray-100"
            }`}
          >
            Collapse All
          </button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr
              className={`text-left text-xs ${
                isDarkMode
                  ? "text-rp-grey/50 border-b border-rp-separator"
                  : "text-gray-500 border-b border-gray-200"
              }`}
            >
              <th className="pb-2 pr-2 font-medium w-8" />
              <th className="pb-2 pr-4 font-medium">ID</th>
              <th className="pb-2 pr-4 font-medium">Citrini ID</th>
              <th className="pb-2 pr-4 font-medium">Name</th>
              <th className="pb-2 pr-4 font-medium">Inception Date</th>
              <th className="pb-2 font-medium">Fetched At</th>
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
            {filteredPortfolios.map((portfolio) => {
              const isExpanded = expandedRows.has(portfolio.id);
              const detail = expandedData.get(portfolio.id);
              const isLoadingDetail = expandLoading.has(portfolio.id);

              return (
                <React.Fragment key={portfolio.id}>
                  <tr
                    onClick={() => toggleRow(portfolio)}
                    className={`cursor-pointer ${
                      isDarkMode ? "hover:bg-rp-surface/50" : "hover:bg-gray-50"
                    }`}
                  >
                    <td className="py-2.5 pr-2">
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      )}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {portfolio.id}
                    </td>
                    <td
                      className={`py-2.5 pr-4 font-mono text-xs ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {portfolio.citriniId}
                    </td>
                    <td
                      className={`py-2.5 pr-4 ${
                        isDarkMode ? "text-rp-grey" : "text-gray-800"
                      }`}
                    >
                      {portfolio.name}
                    </td>
                    <td
                      className={`py-2.5 pr-4 ${
                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                      }`}
                    >
                      {formatDateCell(portfolio.inceptionDateUtc)}
                    </td>
                    <td
                      className={`py-2.5 ${isDarkMode ? "text-rp-grey/70" : "text-gray-700"}`}
                    >
                      {formatDateCell(portfolio.fetchedAtUtc)}
                    </td>
                  </tr>

                  {isExpanded && (
                    <tr>
                      <td colSpan={COLS} className="p-0">
                        <div
                          className={`px-8 py-3 ${
                            isDarkMode ? "bg-rp-surface/30" : "bg-gray-50"
                          }`}
                        >
                          {isLoadingDetail && (
                            <div className="flex items-center py-4">
                              <div
                                className={`animate-spin rounded-full h-5 w-5 border-b-2 mr-3 ${
                                  isDarkMode ? "border-rp-amber" : "border-blue-600"
                                }`}
                              />
                              <span
                                className={`text-sm ${
                                  isDarkMode ? "text-rp-grey/50" : "text-gray-600"
                                }`}
                              >
                                Loading holdings...
                              </span>
                            </div>
                          )}
                          {detail && detail.citriniPortfolioDetails.length === 0 && (
                            <p
                              className={`text-sm py-2 ${
                                isDarkMode ? "text-rp-grey/40" : "text-gray-400"
                              }`}
                            >
                              No holdings
                            </p>
                          )}
                          {detail && detail.citriniPortfolioDetails.length > 0 && (
                            <table className="w-full text-xs">
                              <thead>
                                <tr
                                  className={`text-left ${
                                    isDarkMode
                                      ? "text-rp-grey/40 border-b border-rp-separator"
                                      : "text-gray-400 border-b border-gray-200"
                                  }`}
                                >
                                  <th className="pb-1.5 pr-3 font-medium">Ticker ID</th>
                                  <th className="pb-1.5 pr-3 font-medium">Type</th>
                                  <th className="pb-1.5 pr-3 font-medium">Name</th>
                                  <th className="pb-1.5 pr-3 font-medium">Ticker</th>
                                  <th className="pb-1.5 pr-3 font-medium">ISIN</th>
                                  <th className="pb-1.5 pr-3 font-medium">MIC</th>
                                  <th className="pb-1.5 pr-3 font-medium">Market</th>
                                  <th className="pb-1.5 pr-3 font-medium">Security Type</th>
                                  <th className="pb-1.5 pr-3 font-medium">FIGI</th>
                                  <th className="pb-1.5 pr-3 font-medium text-right">Weight</th>
                                  <th className="pb-1.5 font-medium">Date</th>
                                </tr>
                              </thead>
                              <tbody
                                className={`divide-y ${
                                  isDarkMode ? "divide-rp-separator/50" : "divide-gray-100"
                                }`}
                              >
                                {detail.citriniPortfolioDetails.map((row) => (
                                  <tr key={row.id}>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                      }`}
                                    >
                                      {row.tickerId}
                                    </td>
                                    <td className="py-1.5 pr-3">
                                      <span
                                        className={`inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                                          row.tickerType === "BASKET"
                                            ? "bg-purple-100 text-purple-800"
                                            : "bg-blue-100 text-blue-800"
                                        }`}
                                      >
                                        {row.tickerType}
                                      </span>
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 ${
                                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                      }`}
                                    >
                                      {row.tickerName}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.ticker || "-"}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.isin || "-"}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.mic || "-"}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.market || "-"}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.securityType || "-"}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {`${row.compositeFigi ?? "null"},${row.data?.chinaC1Figi ?? "null"},${row.data?.chinaC2Figi ?? "null"}`}
                                    </td>
                                    <td
                                      className={`py-1.5 pr-3 text-right font-mono ${
                                        isDarkMode ? "text-rp-grey/70" : "text-gray-700"
                                      }`}
                                    >
                                      {row.weight}
                                    </td>
                                    <td
                                      className={`py-1.5 font-mono ${
                                        isDarkMode ? "text-rp-grey/50" : "text-gray-500"
                                      }`}
                                    >
                                      {row.date}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PortfoliosFetchedTab;
